// Independent edit check based on pdf.js text items (pdf.js has its own PDF
// interpreter, so this catches cases where ours misreads a file). Used by the
// app after every edit and by the tests.
import { quote } from '../quote.ts';

export interface TextItemLike {
  str: string;
  transform: number[];
  width: number;
  height: number;
  /** pdf.js puts a right-to-left item's characters in reading order; the engine's text is in drawing order */
  dir?: string;
}

/** An item's text in the order its glyphs are drawn (as the engine reads text). */
const drawn = (it: TextItemLike) => (it.dir === 'rtl' ? [...it.str].reverse().join('') : it.str);

export interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export const overlaps = (a: Box, b: Box) => a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;

export function quadBounds(quads: [number, number][][], grow = 0): Box {
  const xs = quads.flat().map((p) => p[0]);
  const ys = quads.flat().map((p) => p[1]);
  return { x0: Math.min(...xs) - grow, y0: Math.min(...ys) - grow, x1: Math.max(...xs) + grow, y1: Math.max(...ys) + grow };
}

/** User-space bounds of a pdf.js text item (baseline run, plus height and a descender allowance). */
export function itemBounds(it: TextItemLike): Box {
  const [a, b, c, d, e, f] = it.transform;
  const ux = Math.hypot(a, b) || 1;
  const uy = Math.hypot(c, d) || 1;
  const end = [e + (a / ux) * it.width, f + (b / ux) * it.width];
  const h = it.height || uy;
  const pts = [
    [e, f],
    [end[0], end[1]],
    [e + (c / uy) * h, f + (d / uy) * h],
    [end[0] + (c / uy) * h, end[1] + (d / uy) * h],
    [e - (c / uy) * h * 0.3, f - (d / uy) * h * 0.3],
  ];
  return { x0: Math.min(...pts.map((p) => p[0])), y0: Math.min(...pts.map((p) => p[1])), x1: Math.max(...pts.map((p) => p[0])), y1: Math.max(...pts.map((p) => p[1])) };
}

/** The item's writing direction and its start and end along it, and its baseline offset across it. */
function lineOf(it: TextItemLike): { dir: [number, number]; start: number; end: number; across: number } {
  const [a, b, , , e, f] = it.transform;
  const len = Math.hypot(a, b) || 1;
  const dir: [number, number] = [a / len, b / len];
  const start = e * dir[0] + f * dir[1];
  return { dir, start, end: start + it.width, across: -e * dir[1] + f * dir[0] };
}

/** True if `it` starts on the line of `other` (same direction and baseline), within its extent. */
function insideLineOf(it: TextItemLike, other: TextItemLike): boolean {
  const l = lineOf(other);
  const m = lineOf(it);
  if (Math.abs(l.dir[0] - m.dir[0]) > 1e-6 || Math.abs(l.dir[1] - m.dir[1]) > 1e-6) return false;
  const height = Math.max(other.height, it.height, 1);
  return Math.abs(l.across - m.across) <= 0.1 * height && m.start >= l.start - 0.5 && m.start <= l.end + 0.5;
}

const strip = (s: string) => s.replace(/\s+/g, '');
/** How many leading characters two strings share. */
const common = (a: string, b: string) => {
  let k = 0;
  while (k < a.length && k < b.length && a[k] === b[k]) k++;
  return k;
};
/** Start/end agreement for items next to an edit (pt): an item's width is a sum of glyph advances. */
const POSITION_TOLERANCE = 0.01;

/**
 * Text items that are not part of the edit must be identical before and after
 * (string, and position within `tolerance` points). An item is part of the
 * edit if it overlaps an edit region, or starts on the line of such an item
 * (before or after) within its extent: an edit can split an item (a new font
 * starts a new item in pdf.js) or merge two. With `replace`, the text of the
 * items that are part of the edit must read, whitespace ignored, as before
 * with `replace.from` replaced by `replace.to`. Returns problems.
 */
/** Text an edit replaced: `from` became `to` (empty for a delete). */
export type Replacement = { from: string; to: string };

export function compareOutsideRegions(before: TextItemLike[], after: TextItemLike[], regions: Box[], tolerance = 0.001, replace?: Replacement | Replacement[]): string[] {
  const touches = (it: TextItemLike) => it.str.trim() !== '' && regions.some((r) => overlaps(r, itemBounds(it)));
  const touchingBefore = before.filter(touches);
  const touchingAfter = after.filter(touches);
  const partOfEdit = (it: TextItemLike, other: TextItemLike[]) => touches(it) || other.some((o) => insideLineOf(it, o));
  const split = (items: TextItemLike[], other: TextItemLike[]) => {
    const edit: TextItemLike[] = [];
    const rest: TextItemLike[] = [];
    for (const it of items) {
      if (!it.str.trim()) continue;
      (partOfEdit(it, other) ? edit : rest).push(it);
    }
    return { edit, rest };
  };
  const A = split(before, touchingAfter);
  const B = split(after, touchingBefore);
  const problems: string[] = [];
  if (A.rest.length !== B.rest.length) problems.push(`${A.rest.length} text items outside the edit before, ${B.rest.length} after`);
  for (let k = 0; k < Math.min(A.rest.length, B.rest.length) && problems.length < 10; k++) {
    const a = A.rest[k];
    const b = B.rest[k];
    const moved = Math.hypot(b.transform[4] - a.transform[4], b.transform[5] - a.transform[5]);
    if (a.str !== b.str || moved > tolerance) problems.push(`text ${quote(a.str)} became ${quote(b.str)} (moved ${moved.toFixed(4)} pt)`);
  }
  // An item that belongs to the edit without touching its region (a split or merged item on the edited
  // line) must still be in place: its start or its end equals that of an item on the line in the other list.
  const anchored = (it: TextItemLike, other: TextItemLike[]) => {
    const l = lineOf(it);
    return other.some((o) => {
      const m = lineOf(o);
      const sameLine = Math.abs(l.dir[0] - m.dir[0]) <= 1e-6 && Math.abs(l.dir[1] - m.dir[1]) <= 1e-6 && Math.abs(l.across - m.across) <= 0.1 * Math.max(it.height, o.height, 1);
      return sameLine && (Math.abs(l.start - m.start) <= POSITION_TOLERANCE || Math.abs(l.end - m.end) <= POSITION_TOLERANCE);
    });
  };
  for (const [items, other, when] of [[A.edit, after, 'before'], [B.edit, before, 'after']] as const) {
    for (const it of items) {
      if (problems.length >= 10) break;
      if (!touches(it) && !anchored(it, other.filter((o) => o.str.trim()))) problems.push(`text ${quote(it.str)} (${when} the edit) next to the edit moved: neither its start nor its end matches`);
    }
  }
  if (replace) {
    const was = strip(A.edit.map(drawn).join(''));
    const is = strip(B.edit.map(drawn).join(''));
    // Each replacement applies once, somewhere in what the edit touched (several for a multi-object delete).
    const list = Array.isArray(replace) ? replace : [replace];
    let options = new Set([was]);
    for (const r of list) {
      const from = strip(r.from);
      const next = new Set<string>();
      // An empty `from` is text added where nothing was read before (recognised words, M6): inserted anywhere.
      if (!from) for (const w of options) for (let i = 0; i <= w.length; i++) next.add(w.slice(0, i) + strip(r.to) + w.slice(i));
      else for (const w of options) for (let i = w.indexOf(from); i >= 0; i = w.indexOf(from, i + 1)) next.add(w.slice(0, i) + strip(r.to) + w.slice(i + from.length));
      options = next;
      if (options.size > 10000) break;
    }
    if (!options.has(is)) {
      // Only around where it first differs from the nearest expected reading (a whole page's text is unreadable in a message).
      const near = [...options].reduce((a, b) => (common(b, is) > common(a, is) ? b : a));
      const at = common(near, is);
      const cut = (t: string) => `${at > 30 ? '…' : ''}${t.slice(Math.max(0, at - 30), at + 50)}${t.length > at + 50 ? '…' : ''}`;
      const short = (t: string) => (t.length > 60 ? `${t.slice(0, 60)}…` : t);
      const what = list.map((r) => (r.to ? `${quote(short(r.from))} replaced by ${quote(short(r.to))}` : `${quote(short(r.from))} removed`)).join(', ');
      problems.push(`the edited ${list.length === 1 && list[0].to !== '' ? 'line' : 'text'} reads ${quote(cut(is))} where ${quote(cut(near))} was expected (the original with ${what})`);
    }
  }
  return problems;
}
